// ============================================================================
// CHANGO V5.0 — Ops Company Name Change Review Queue
// ============================================================================
// Task:     §9.6.2e Ops queue for company name change requests
// Purpose:  List pending / approved / rejected requests + approve/reject
// Reference: §6 API endpoints, §11.7 validation
// ============================================================================

import React, { useEffect, useState } from 'react';
import { api, HttpError } from '../../api/client.js';

const STATUS_LABEL = {
  pending: { label: '待审核', bg: 'bg-amber-50', text: 'text-amber-700', badge: 'badge-amber' },
  approved: { label: '已通过', bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'badge-emerald' },
  rejected: { label: '已拒绝', bg: 'bg-red-50', text: 'text-red-700', badge: 'badge-red' }
};

function RequestRow({ req, onApprove, onReject, busy }) {
  const [expanding, setExpanding] = useState(false);
  const status = STATUS_LABEL[req.status] || STATUS_LABEL.pending;
  const ownerLabel = req.owner_display_name || req.owner_name || req.owner_email || '—';

  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-3 px-4 text-sm">{req.company_name}</td>
      <td className="py-3 px-4 text-xs text-slate-600">
        <div className="font-medium text-slate-800">{ownerLabel}</div>
        <div className="text-slate-500">{req.owner_email || '—'}</div>
      </td>
      <td className="py-3 px-4 text-xs">
        <span className={`rounded px-2 py-1 text-xs font-medium ${status.text} ${status.bg}`}>
          {status.label}
        </span>
      </td>
      <td className="py-3 px-4 text-xs">
        <div className="space-y-1">
          <div className="font-mono text-slate-700">{req.company_name}</div>
          <div className="text-slate-500">→</div>
          <div className="font-mono text-slate-800 font-semibold">{req.requested_company_name}</div>
        </div>
      </td>
      <td className="py-3 px-4">
        <button
          type="button"
          className="text-sm text-chango-700 hover:underline disabled:text-slate-400"
          onClick={() => setExpanding(!expanding)}
          disabled={busy}
        >
          {expanding ? '▼ 隐藏' : '▶ 查看执照'}
        </button>
        {expanding && (
          <div className="mt-3 space-y-2">
            {req.license_file_url && (
              <>
                <p className="text-xs text-slate-600">旧执照：</p>
                <a
                  href={req.license_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-chango-600 hover:underline"
                >
                  打开图片
                </a>
              </>
            )}
            {req.requested_license_file_url && (
              <>
                <p className="text-xs text-slate-600">新执照：</p>
                <a
                  href={req.requested_license_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-chango-600 hover:underline"
                >
                  打开图片
                </a>
              </>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-4 text-xs text-slate-500">
        {new Date(req.created_at).toLocaleDateString('zh-CN')}
      </td>
      <td className="py-3 px-4 text-xs">
        {req.attempt_seq >= 3 && (
          <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-semibold">
            重试 #{req.attempt_seq}
          </span>
        )}
      </td>
      <td className="py-3 px-4 text-right space-x-2">
        {req.status === 'pending' && (
          <>
            <button
              type="button"
              className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded text-xs hover:bg-emerald-200 disabled:opacity-50"
              onClick={() => onApprove(req.id)}
              disabled={busy}
            >
              ✓ 通过
            </button>
            <button
              type="button"
              className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 disabled:opacity-50"
              onClick={() => onReject(req.id)}
              disabled={busy}
            >
              ✗ 拒绝
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

export default function OpsCompanyNameChangeQueue() {
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectionModal, setRejectionModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Load requests on mount / status change
  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const res = await api.get(`/api/ops/company-name-changes/?status=${status}`);
        if (ignore) return;

        setRequests(Array.isArray(res.requests) ? res.requests : []);
      } catch (err) {
        if (!ignore) {
          setError(
            err instanceof HttpError ? err.message : '加载失败，请刷新重试'
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => { ignore = true; };
  }, [status]);

  const handleApprove = async (requestId) => {
    if (!window.confirm('确认通过此变更申请？')) return;

    setBusy(true);
    try {
      await api.post(`/api/ops/company-name-changes/${requestId}/approve`);
      setRequests(requests.filter(r => r.id !== requestId));
      alert('已通过并应用变更');
    } catch (err) {
      alert(
        err instanceof HttpError
          ? `操作失败: ${err.message}`
          : '操作失败，请重试'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReject = (requestId) => {
    setRejectionModal(requestId);
    setRejectionReason('');
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim() || rejectionReason.length < 10) {
      alert('拒绝理由至少 10 个字符');
      return;
    }

    setBusy(true);
    try {
      await api.post(`/api/ops/company-name-changes/${rejectionModal}/reject`, {
        reason: rejectionReason.trim()
      });
      setRequests(requests.filter(r => r.id !== rejectionModal));
      setRejectionModal(null);
      setRejectionReason('');
      alert('已拒绝申请并通知客户');
    } catch (err) {
      alert(
        err instanceof HttpError
          ? `操作失败: ${err.message}`
          : '操作失败，请重试'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">公司名变更审核队列</h1>
        <p className="mt-2 text-sm text-slate-600">§9.6.2e 公司全称变更审核和管理</p>
      </div>

      {/* Status Tabs */}
      <div className="mb-6 flex gap-2 border-b border-slate-200">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            type="button"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[2px] ${
              status === s
                ? 'border-chango-500 text-chango-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setStatus(s)}
          >
            {STATUS_LABEL[s]?.label} {requests.length > 0 && `(${requests.length})`}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-6 text-slate-500">加载中...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-8 text-slate-400">暂无申请</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b-2 border-slate-200 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">公司名</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">负责人</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">状态</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">旧 → 新</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">执照</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">申请时间</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">重试</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <RequestRow
                  key={req.id}
                  req={req}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  busy={busy}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-slate-800">拒绝申请</h2>
            <p className="mb-4 text-sm text-slate-600">请输入拒绝理由（至少 10 个字符）</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="输入拒绝理由..."
              className="mb-4 block w-full rounded border border-slate-200 px-3 py-2 text-sm"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary flex-1"
                onClick={() => setRejectionModal(null)}
                disabled={busy}
              >
                取消
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={submitRejection}
                disabled={rejectionReason.length < 10 || busy}
              >
                {busy ? '处理中...' : '确认拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
